/**
 * S15-live activation — turn the active view name into the board's projection plan.
 *
 * The board pipeline (`useBoardLayout`) consumes a `ViewProjectionPlan | null`;
 * `null` (no active view) is the identity projection (byte-identical default board).
 * This helper resolves the named view off the in-browser resolved engine model and
 * runs `planView` over a fresh eval context. Kept as a pure, testable function
 * outside the board surface so the activation logic is gated without a DOM mount.
 *
 * ## Evaluation-context completion (GAP 3)
 * `planView` is only as smart as the {@link EvalContext} it is handed. This module
 * assembles the FULL context the app can supply in-browser:
 *   - `policies` / `queries` — the workspace governance closure, so a
 *     `style violating <P>` (or a query-ref selector) actually resolves instead of
 *     silently no-opping. Built by {@link collectViewGovernance} from the exact
 *     sources the model compiled from (parse is content-cached, so this is free).
 *   - `viewsByName` — planView's 3rd arg, so a view INSTANCE resolves its parent
 *     (merging its clauses + binding its knobs) live.
 *   - capabilities — intentionally left UNSET. No `ver`/`tel` provider exists
 *     in-browser, so a `@ver.*` capability getter routes to the absent-capability
 *     sink and degrades INERT (undefined → no-data) rather than throwing.
 *
 * ## Crash-safety (never a white screen)
 * `computeViewPlanResult` wraps the whole projection in a try/catch: a throw from
 * ANY selector/expression path degrades to the identity plan (`null`) plus a
 * surfaced diagnostic, so a pathological view can never take down the board (this
 * runs inside a React `useMemo` — an uncaught throw there unmounts the tree).
 */
import {
  buildNodeUniverse,
  buildClosure,
  collectGovernance,
  collectScopedPolicies,
  parseCached,
  planView,
  resolveKnobBindings,
  resolveViewInstance,
  type EvalContext,
  type ResolvedModel,
  type DependencyGraph,
  type Diagnostic,
  type PolicyDecl,
  type QueryDecl,
  type ViewDeclV2,
  type ViewClause,
  type ViewProjectionPlan as EngineViewProjectionPlan,
} from "@archlang/engine";
import { effectivePlane, planeClause, type Vfs } from "@archlang/viewer-core";
import type { ViewProjectionPlan } from "@archlang/scene/arch-model";
import { viewsByNameOf } from "./viewRepr.js";

/** Diagnostic code stamped when a view projection throws — surfaced in the HUD. */
export const VIEW_PLAN_ERROR = "VIEW_PLAN_ERROR";

/**
 * The workspace governance closure a view projection evaluates against — the
 * policy/query vocabulary a `style violating <P>` or a query-ref selector reads.
 * `policies` is the name→decl map `ctx.policies` wants; `policyList` is the same
 * set as an ordered list (the shape `advisoryFindings` consumes, so the advisory
 * report can reuse it).
 */
export interface ViewGovernance {
  readonly policies: ReadonlyMap<string, PolicyDecl>;
  readonly queries: ReadonlyMap<string, QueryDecl>;
  readonly policyList: readonly PolicyDecl[];
}

/**
 * Collect the workspace governance (policies + queries) from the sources the
 * model was compiled from. Mirrors the LSP's `computePolicyDiagnostics`:
 * module-body policies come from the resolved tree (`collectScopedPolicies`,
 * `ownerPath` stamped), file-root ones from each file's top-level declarations.
 * Parsing is content-cached (`parseCached`) — the pipeline already parsed these
 * identical bytes, so this is a cache hit, not a re-parse. Returns null when
 * either input is missing (identity board — nothing to govern).
 */
export function collectViewGovernance(
  model: ResolvedModel | null,
  sources: Vfs | null,
): ViewGovernance | null {
  if (!model || !sources) return null;
  const files = [...sources].map(([uri, content]) => parseCached(content, uri).file);
  const topLevelPolicies: PolicyDecl[] = [];
  for (const f of files) {
    for (const d of f.declarations) if (d.kind === "policy") topLevelPolicies.push(d.decl);
  }
  const scoped = collectScopedPolicies(model, topLevelPolicies);
  const policies = new Map<string, PolicyDecl>();
  for (const p of scoped.policies) if (!policies.has(p.name)) policies.set(p.name, p);
  // `collectGovernance` walks the same files for the query vocabulary (file-root
  // + module-body). Its policy set is the lexical closure; we prefer the
  // `ownerPath`-scoped list above for the `violating`/advisory surfaces.
  const gov = collectGovernance(files);
  return { policies, queries: gov.queries, policyList: scoped.policies };
}

/**
 * A view projection plus the diagnostics it produced (the view's OWN health —
 * unresolved refs, sort mismatches, unknown policy/knob — plus any crash
 * diagnostic). `plan` is what the board consumes (`null` = identity board);
 * `diagnostics` is surfaced in the board HUD near the view picker.
 */
export interface ViewPlanResult {
  readonly plan: ViewProjectionPlan | null;
  readonly diagnostics: readonly Diagnostic[];
}

/**
 * The board's projection plan for the active view AND/OR an active plane pick.
 * A plane selection (S16) is a session-ephemeral `on <plane>` binding — it
 * composes onto any active view's clauses (else stands alone), so the plane
 * dropdown narrows the board to that plane's presence (a SUBSET, not a highlight).
 * Returns null (identity board) when neither is active.
 */
export function computeViewPlan(
  resolvedModel: ResolvedModel | null,
  graph: DependencyGraph | null,
  activeViewName: string | null,
  activePlane: string | null = null,
  knobValues: Readonly<Record<string, number | string>> = {},
  /** An in-browser DRAFT view (WYSIWYG builder) — takes precedence over the
   *  named lookup; the board projects a view the model text doesn't hold yet. */
  draftView: ViewDeclV2 | null = null,
  /** Workspace governance closure ({@link collectViewGovernance}) — wires
   *  `ctx.policies`/`ctx.queries` so `style violating <P>` resolves. */
  governance: ViewGovernance | null = null,
): ViewProjectionPlan | null {
  return computeViewPlanResult(
    resolvedModel,
    graph,
    activeViewName,
    activePlane,
    knobValues,
    draftView,
    governance,
  ).plan;
}

/**
 * Like {@link computeViewPlan} but also returns the projection's diagnostics
 * (surfaced by the board HUD) and never throws — a projection failure degrades
 * to the identity plan plus a {@link VIEW_PLAN_ERROR} diagnostic.
 */
export function computeViewPlanResult(
  resolvedModel: ResolvedModel | null,
  graph: DependencyGraph | null,
  activeViewName: string | null,
  activePlane: string | null = null,
  knobValues: Readonly<Record<string, number | string>> = {},
  draftView: ViewDeclV2 | null = null,
  governance: ViewGovernance | null = null,
  revealedKeys: ReadonlySet<string> | null = null,
): ViewPlanResult {
  const { plan, diagnostics } = computeViewSurfaceResult(
    resolvedModel,
    graph,
    activeViewName,
    activePlane,
    knobValues,
    draftView,
    governance,
    revealedKeys,
  );
  return { plan, diagnostics };
}

/**
 * {@link ViewPlanResult} plus what the NON-board representation surfaces
 * (table / matrix / flow) need: the EFFECTIVE view and the completed eval
 * context. One computation path for every surface of the active view — the
 * board consumes `plan`, the table/matrix consume `view` + `ctx` (+ `plan`
 * for the shown-row selection) — so the surfaces can never disagree on
 * instance resolution, governance, knobs, or crash posture.
 */
export interface ViewSurfaceResult extends ViewPlanResult {
  /** The EFFECTIVE view: draft/named pick, plane-composed, live-knob-bound,
   *  and INSTANCE-RESOLVED (the parent's clauses merged in — a table-carrying
   *  parent's instance carries the `table` clause here). Null when no view is
   *  active or the projection crashed (surfaces render nothing + diagnostic). */
  readonly view: ViewDeclV2 | null;
  /** The eval context the plan was computed under — governance wired, knob
   *  bindings resolved — the exact ctx `computeTableData`/`computeMatrixData`
   *  evaluate cells against. Null iff `view` is null. */
  readonly ctx: EvalContext | null;
  /** The SAME plan object under its engine type — for handing back into engine
   *  consumers (`computeTableData`/`computeMatrixData`). `plan` carries the
   *  scene mirror type the board pipeline consumes; the mirror is structural,
   *  so this is a typing seam, not a second computation. */
  readonly enginePlan: EngineViewProjectionPlan | null;
  /** The REGISTERED source view object (the actual `resolvedModel.views[i]`
   *  reference, pristine source-ordered clauses) — present ONLY when the active
   *  view is directly write-back-eligible: a registered, directly-authored view
   *  that is not a live draft, not knob-bound, and not a view instance. Null
   *  otherwise. WYSIWYG write-back keys off THIS (its clauses carry the byte
   *  spans mutations address); `view` is a per-render projected copy whose
   *  identity/order can't be trusted for editing. */
  readonly registeredView: ViewDeclV2 | null;
}

const NO_SURFACE: ViewSurfaceResult = {
  plan: null,
  diagnostics: [],
  view: null,
  ctx: null,
  enginePlan: null,
  registeredView: null,
};

/**
 * The full view-surface computation ({@link ViewSurfaceResult}). Never throws:
 * a projection failure degrades to the identity plan + a {@link VIEW_PLAN_ERROR}
 * diagnostic (this runs inside render `useMemo`s — an uncaught throw unmounts
 * the tree on EVERY surface, not just the board).
 */
export function computeViewSurfaceResult(
  resolvedModel: ResolvedModel | null,
  graph: DependencyGraph | null,
  activeViewName: string | null,
  activePlane: string | null = null,
  knobValues: Readonly<Record<string, number | string>> = {},
  draftView: ViewDeclV2 | null = null,
  governance: ViewGovernance | null = null,
  /** Click-reveal (VIEWS §7): keys the user revealed from boundary stubs —
   *  exact-key union applied engine-side AFTER hide + `on` narrowing. */
  revealedKeys: ReadonlySet<string> | null = null,
): ViewSurfaceResult {
  if (!resolvedModel || !graph) return NO_SURFACE;
  // Qualified name first so a module-scoped view shadowed on its bare name
  // stays addressable (the headless surfaces' addressing rule) — ONE lookup
  // for every surface (board/table/matrix/flow).
  const findView = (name: string) =>
    resolvedModel.views.find((v) => [...(v.ownerPath ?? []), v.name].join(".") === name) ??
    resolvedModel.views.find((v) => v.name === name);
  const named = draftView ?? (activeViewName ? findView(activeViewName) : undefined);
  if (!draftView && activeViewName && !named) return NO_SURFACE;
  // v0.9.0 §1.2: a partial-presence block declares members that are ABSENT from
  // primary. Nothing hides them on an unprojected board, so a model carrying
  // any such member narrows by default — `ctx.defaultPlane` below, the same
  // seam the headless backends use, which hides exactly those members and
  // leaves every node where it was. Models without partial presence (every
  // model until one uses it) keep the identity board untouched.
  //
  // An EXPLICIT pick (the plane dropdown) is a different question — "show me
  // this plane" — so it composes as a real `on` clause and narrows nodes too.
  // A view that names its own plane answers the question itself; the engine
  // decides that off the INSTANCE-RESOLVED clauses, so `view Base Deploy {}`
  // inherits its parent's `on deployment` instead of being widened by a
  // default it never asked for.
  const plane = effectivePlane(resolvedModel, activePlane);
  if (!named && !plane) return NO_SURFACE;

  try {
    const clauses = activePlane
      ? [...(named?.clauses ?? []), planeClause(activePlane)]
      : (named?.clauses ?? []);
    // S18: live knob-picker values become instance bindings the engine's
    // resolveKnobBindings consumes (drives `$knob` / `within $knob`). They
    // OVERLAY the view's own declared bindings (an active INSTANCE keeps its
    // untouched bindings) — replacing the whole array would silently reset
    // every knob the user didn't turn to the parent's decl default.
    const liveBindings = Object.entries(knobValues).map(([name, value]) => ({
      name,
      value: typeof value === "number" ? { kind: "number", value } : { kind: "identifier", text: value },
    }));
    const liveNames = new Set(liveBindings.map((b) => b.name));
    const base = named ?? { name: "__view__", header: { fields: [], knobs: [] }, clauses: [] as ViewClause[] };
    const declared =
      ((base as { bindings?: readonly { name: string; value: unknown }[] }).bindings ?? []).filter(
        (b) => !liveNames.has(b.name),
      );
    const bindings = [...declared, ...liveBindings];
    const view = { ...base, clauses, ...(bindings.length ? { bindings } : {}) } as unknown as ViewDeclV2;

    // viewsByName lets a view INSTANCE (`view <Parent> <Name> { … }`) resolve its
    // parent live — merging the parent's clauses + binding its knobs. Shared
    // with the representation switch (viewsByNameOf: FIRST declaration wins on
    // a shadowed bare name) — an inline last-wins Map here disagreed with it
    // on owner-scoped twins (r35).
    const viewsByName = viewsByNameOf(resolvedModel.views);

    const baseCtx: EvalContext = {
      universe: buildNodeUniverse(resolvedModel),
      graph,
      closure: buildClosure(graph),
      presence: resolvedModel.presence,
      // The default plane, for a board (or view) that names none — see above.
      ...(plane && !activePlane ? { defaultPlane: plane } : {}),
      // Governance closure — `style violating <P>` + query-ref selectors.
      policies: governance?.policies,
      queries: governance?.queries,
      // capabilities intentionally UNSET — `@ver.*` degrades inert in-browser.
    };
    const plan = planView(
      view,
      baseCtx,
      viewsByName,
      revealedKeys && revealedKeys.size > 0 ? revealedKeys : undefined,
    );

    // The representation surfaces read the same pair planView derives
    // internally: the instance-resolved EFFECTIVE view (parent clauses merged,
    // so a `table` on the parent reaches its instances) evaluated under the
    // knob-bound, owner-scoped ctx. Derived here — never re-implemented per
    // surface.
    const { view: effective } = resolveViewInstance(view, viewsByName);
    const scoped: EvalContext =
      effective.ownerPath && effective.ownerPath.length > 0
        ? { ...baseCtx, scopePath: effective.ownerPath }
        : baseCtx;
    const ctx: EvalContext = { ...scoped, knobs: resolveKnobBindings(effective, baseCtx.universe).knobs };

    // Write-back eligibility (WYSIWYG rail editor): only a registered,
    // directly-authored view whose clauses ARE the source bytes may be edited.
    // A live draft (`named === draftView`, not in the model), a knob-bound view
    // (clause values are live-projected), a view instance (clauses merged from a
    // parent), or a plane-scoped projection (an `on` clause is appended and the
    // bands are laid out over the NARROWED process, so a band's sourceClauseIndex
    // can diverge from the pristine clause list) has no stable clause identity to
    // write — fail closed.
    const registeredView =
      !draftView && named && !activePlane && Object.keys(knobValues).length === 0 && !named.instanceOf
        ? (named as ViewDeclV2)
        : null;

    // The engine `ViewProjectionPlan` is the structural source of the scene mirror
    // `applyProjection` consumes (proven byte-equivalent by the through-view gate).
    return {
      plan: plan as ViewProjectionPlan | null,
      diagnostics: (plan?.diagnostics ?? []) as readonly Diagnostic[],
      view: effective,
      ctx,
      enginePlan: plan,
      registeredView,
    };
  } catch (e) {
    // A projection throw must never unmount a surface (these run in useMemos).
    // Degrade to the identity plan + surface the failure as a diagnostic.
    const label = draftView?.name ?? activeViewName ?? "view";
    return {
      plan: null,
      view: null,
      ctx: null,
      enginePlan: null,
      registeredView: null,
      diagnostics: [
        {
          severity: "error",
          code: VIEW_PLAN_ERROR,
          message: `View "${label}" could not be projected: ${e instanceof Error ? e.message : String(e)}`,
        },
      ],
    };
  }
}
